import React, { forwardRef, useState, useEffect } from 'react';
import { actions } from '@ecosystem/newron-design-system';
import { Cells, Table } from '../../design/table2';
import { DataColumn } from '../../types';
import { SegmentDataSet, SegmentedChartData, getDataPeriodCount } from './data';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';

type Props = {
  data: SegmentedChartData;
  xColumn: DataColumn;
  chartFullscreen?: boolean;
};
const SegmentationTable = forwardRef<HTMLDivElement, Props>(({ data, xColumn, chartFullscreen }, ref) => {
  const [rowsToDisplay, setRowsToDisplay] = useState<number>(100);

  const firstData = data.segmentDataSets[0];
  const rowCount = firstData.dataSets.length;
  const includeTotals = firstData.dataSets !== undefined;

  const handleLoadMore = () => {
    const newAmount = rowsToDisplay + 100;
    if (newAmount > rowCount) {
      setRowsToDisplay(rowCount);
    } else {
      setRowsToDisplay(newAmount);
    }
  };
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight
  );

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  const colSpan = data.segmentDataSets.length * data.dataLabels.length;

  const PERIOD_COUNT = getDataPeriodCount(data);

  const { Button } = actions;

  const { isMobile } = useMobile();

  return (
    <div
      ref={ref}
      className={classNames('relative w-full', {
        'chart-h-70vh mt-7': isMobile && !(isLandscape || chartFullscreen),
        'chart-h-90vh mt-7': isMobile && (isLandscape || chartFullscreen),
      })}
    >
      <Table>
        <Table.Header withRows={false} isStickyTop={isMobile}>
          <tr>
            <Table.Column colSpan={1}> </Table.Column>
            <Table.Column align="center" borders="lrb" colSpan={colSpan} isStickyLeft={isMobile}>
              {data.yLabel}
            </Table.Column>
          </tr>
          <tr>
            <Table.Column rowSpan={2} isStickyLeft={isMobile}>
              {xColumn.label}
            </Table.Column>
            {data.segmentDataSets.map((set, idx) => (
              <SegmentationHeadersTop key={`ht_${idx}`} set={set} dataLabels={data.dataLabels} />
            ))}
          </tr>
          <tr>
            {data.segmentDataSets.map((set, idx) => (
              <SegmentationHeadersBottom key={`ht_${idx}`} set={set} dataLabels={data.dataLabels} />
            ))}
          </tr>
        </Table.Header>
        <Table.Body>
          {includeTotals && (
            <Table.Row important>
              <Table.Cell align="left" isStickyLeft={isMobile}>
                Market
              </Table.Cell>
              {data.segmentDataSets.map((set, idx) => (
                <SegmentationCell key={`sum_${idx}`} set={set} rowDataSetId="totals" periodCount={PERIOD_COUNT} />
              ))}
            </Table.Row>
          )}
          {firstData.dataSets
            .filter(s => !!s.uniqueId)
            .slice(0, rowsToDisplay)
            .map((dataSet, rIdx) => (
              <Table.Row key={`r_${dataSet.uniqueId}`}>
                <Table.Cell align="left" isStickyLeft={isMobile}>
                  {dataSet.label}
                </Table.Cell>
                {data.segmentDataSets.map((set, idx) => (
                  <SegmentationCell
                    key={`r_${dataSet.uniqueId}_${rIdx}_${idx}`}
                    set={set}
                    rowDataSetId={dataSet.uniqueId}
                    periodCount={PERIOD_COUNT}
                  />
                ))}
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
      <div className="py-4 flex items-center gap-4">
        <span className="">
          {rowsToDisplay > rowCount && `${rowCount} out of ${rowCount} items`}
          {rowsToDisplay <= rowCount && `${rowsToDisplay} out of ${rowCount} items`}
        </span>
        {rowsToDisplay <= rowCount && <Button onClick={handleLoadMore}>Load more</Button>}
      </div>
    </div>
  );
});

type SegmentationHeadersProps = {
  label?: string;
  set?: SegmentDataSet;
  dataLabels?: (string | string[])[];
};

function SegmentationHeadersTop({ label, set, dataLabels }: SegmentationHeadersProps) {
  return (
    <Table.Column align="center" borders="lrb" colSpan={dataLabels.length}>
      {set?.label || label}
    </Table.Column>
  );
}

function SegmentationHeadersBottom({ dataLabels }: SegmentationHeadersProps) {
  return (
    <>
      {dataLabels.map((label, idx) => (
        <Table.Column
          align="center"
          key={`sr_${idx}`}
          borders={`b${idx === 0 ? 'l' : idx === dataLabels.length - 1 ? 'r' : ''}`}
        >
          <HeaderLabel label={label} />
        </Table.Column>
      ))}
    </>
  );
}

type LabelProps = {
  label: string | string[];
};
function HeaderLabel({ label }: LabelProps) {
  if (Array.isArray(label)) {
    return (
      <>
        {label.map(l => (
          <span key={l} className="block nowrap">
            {l}
          </span>
        ))}
      </>
    );
  }

  return <span className="block nowrap">{label}</span>;
}

type SegmentationCellProps = {
  rowDataSetId: 'totals' | string;
  set: SegmentDataSet;
  periodCount: number;
};
function SegmentationCell({ rowDataSetId, set, periodCount }: SegmentationCellProps) {
  const setData = rowDataSetId === 'totals' ? set.totals : set.dataSets.find(set => set.uniqueId === rowDataSetId);

  return (
    <>
      {Array(periodCount)
        .fill(null)
        .map((_, idx) => (
          <Table.Cell
            borders={`b${idx === 0 ? 'l' : idx === periodCount - 1 ? 'r' : ''}`}
            key={`row_${rowDataSetId}_${setData?.label}_${idx}`}
          >
            {setData !== undefined ? setData.values.formatted[idx] || '-' : '-'}
            {setData !== undefined && (
              <Cells.ValueChange value={setData.changes?.formatted[idx]} raw={setData.changes?.raw[idx]} />
            )}
          </Table.Cell>
        ))}
    </>
  );
}

export { SegmentationTable };
